import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, UserCog, BarChart3, LogOut, FileEdit } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["admin", "staff", "client"] },
  { to: "/staff", icon: UserCog, label: "Staff", roles: ["admin"] },
  { to: "/reports", icon: BarChart3, label: "Reports", roles: ["admin"] },
  { to: "/content", icon: FileEdit, label: "Content Manager", roles: ["admin"], group: "cms" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV_ITEMS.filter((i) => i.roles.includes(user?.role || ""));

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      <aside className="hidden md:flex w-56 flex-col border-r border-[#E2E8F0] bg-white sticky top-0 h-screen">
        <div className="px-4 py-4 border-b border-[#E2E8F0] flex items-center gap-2">
          <img src="/brand/ndm-logo.jpg" alt="NDM" className="h-8 w-8 rounded-md object-cover" />
          <div>
            <div className="text-sm font-semibold text-[#0F172A] leading-none">NDM Advisors</div>
            <div className="text-[10px] uppercase tracking-wider text-[#64748B] mt-1">
              Compliance Dashboard
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-0.5">
          {items.map((it, idx) => {
            const prev = items[idx - 1];
            const showSep = it.group && prev && !prev.group;
            return (
              <React.Fragment key={it.to}>
                {showSep && (
                  <div className="my-2 border-t border-[#E2E8F0]" />
                )}
                <NavLink
                  to={it.to}
                  end={it.to === "/"}
                  data-testid={`nav-${it.to === "/" ? "dashboard" : it.to.slice(1)}`}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex items-center gap-2 h-9 px-2.5 rounded-md text-sm font-medium",
                      isActive
                        ? "bg-[#0F172A] text-white"
                        : "text-[#334155] hover:bg-[#F1F5F9]"
                    )
                  }
                >
                  <it.icon className="h-4 w-4" />
                  {it.label}
                </NavLink>
              </React.Fragment>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#E2E8F0]">
          <div className="text-xs text-[#0F172A] font-medium truncate">
            {user?.name || user?.email}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[#64748B]">
            {user?.role}
          </div>
          <button
            type="button"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
            data-testid="sidebar-logout"
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#334155] hover:text-[#DC2626]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
